// src/ui/skinView.js
import {
  THEME,
  drawGlassPanel,
  drawNeonBackground,
  drawPillButton,
  drawSmallText,
  drawTopBar,
  getCanvasSize,
  roundedPath
} from "./theme.js";

import { normalizeLanguage, translate, getSkinName } from "../../../i18n/messages.js";

// ============================================================
// 创意皮肤数据（最前面保留原始经典）
// ============================================================
export const SKIN_DATA = {
  ball: [
    { id: 'ball_default', name: 'ball_default', emoji: '⚪', type: 'default' },
    { id: 'ball_star', name: 'ball_star', emoji: '⭐', type: 'star' },
    { id: 'ball_football', name: 'ball_football', emoji: '⚽', type: 'football' },
    { id: 'ball_flower', name: 'ball_flower', emoji: '🌸', type: 'flower' },
    { id: 'ball_planet', name: 'ball_planet', emoji: '🪐', type: 'planet' },
    { id: 'ball_rainbow', name: 'ball_rainbow', emoji: '🌈', type: 'rainbow' },
    { id: 'ball_diamond', name: 'ball_diamond', emoji: '💎', type: 'diamond' }
  ],
  paddle: [
    { id: 'paddle_default', name: 'paddle_default', emoji: '▬', type: 'default' },
    { id: 'paddle_stool', name: 'paddle_stool', emoji: '🪑', type: 'stool' },
    { id: 'paddle_grass', name: 'paddle_grass', emoji: '🌿', type: 'grass' },
    { id: 'paddle_rainbow', name: 'paddle_rainbow', emoji: '🌈', type: 'paddle_rainbow' },
    { id: 'paddle_carpet', name: 'paddle_carpet', emoji: '🧙', type: 'carpet' },
    { id: 'paddle_wave', name: 'paddle_wave', emoji: '🌊', type: 'wave' },
    { id: 'paddle_mecha', name: 'paddle_mecha', emoji: '🤖', type: 'mecha' }
  ]
};

function skinText(language) {
  const lang = normalizeLanguage(language);
  return {
    title: translate(lang, "skinsTitle"),
    selected: translate(lang, "selected")
  };
}

function skinCategoryName(category, language) {
  return translate(
    normalizeLanguage(language),
    category === "ball" ? "ballSkins" : "paddleSkins"
  );
}

function skinName(skin, language) {
  return getSkinName(skin.id, language);
}

// ============================================================
// 绘制创意小球预览（支持 default 经典白）
// ============================================================
export function drawBallPreview(ctx, cx, cy, radius, type) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.clip();

  switch (type) {
    case 'default': {
      // 经典白
      const grad = ctx.createRadialGradient(cx - radius*0.3, cy - radius*0.3, 2, cx, cy, radius);
      grad.addColorStop(0, '#ffffff');
      grad.addColorStop(0.7, '#e0f0ff');
      grad.addColorStop(1, '#a0c4e8');
      ctx.fillStyle = grad;
      ctx.shadowColor = '#52f6e8';
      ctx.shadowBlur = 12;
      ctx.fillRect(cx - radius, cy - radius, radius*2, radius*2);
      // 高光
      ctx.shadowBlur = 0;
      ctx.fillStyle = 'rgba(255,255,255,0.6)';
      ctx.beginPath();
      ctx.arc(cx - radius*0.25, cy - radius*0.25, radius*0.3, 0, Math.PI*2);
      ctx.fill();
      break;
    }
    case 'star': {
      const outerR = radius * 0.9;
      const innerR = radius * 0.4;
      ctx.fillStyle = '#ffd700';
      ctx.shadowColor = '#ffd700';
      ctx.shadowBlur = 12;
      ctx.beginPath();
      for (let i = 0; i < 10; i++) {
        const angle = -Math.PI / 2 + (i * Math.PI) / 5;
        const r = i % 2 === 0 ? outerR : innerR;
        if (i === 0) ctx.moveTo(cx + Math.cos(angle) * r, cy + Math.sin(angle) * r);
        else ctx.lineTo(cx + Math.cos(angle) * r, cy + Math.sin(angle) * r);
      }
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'football': {
      ctx.shadowBlur = 0;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(cx - radius, cy - radius, radius*2, radius*2);
      ctx.strokeStyle = '#1a1a1a';
      ctx.lineWidth = 1.5;
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2 - Math.PI / 2;
        const px = cx + Math.cos(a) * radius * 0.6;
        const py = cy + Math.sin(a) * radius * 0.6;
        ctx.fillStyle = '#1a1a1a';
        ctx.beginPath();
        for (let j = 0; j < 5; j++) {
          const b = (j / 5) * Math.PI * 2 + a;
          const d = j === 0 ? radius * 0.28 : radius * 0.28;
          const sx = px + Math.cos(b) * d;
          const sy = py + Math.sin(b) * d;
          if (j === 0) ctx.moveTo(sx, sy);
          else ctx.lineTo(sx, sy);
        }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
      }
      ctx.strokeStyle = '#1a1a1a';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.stroke();
      break;
    }
    case 'flower': {
      ctx.shadowBlur = 0;
      const petalCount = 6;
      const petalR = radius * 0.55;
      for (let i = 0; i < petalCount; i++) {
        const a = (i / petalCount) * Math.PI * 2 - Math.PI / 2;
        const px = cx + Math.cos(a) * radius * 0.4;
        const py = cy + Math.sin(a) * radius * 0.4;
        ctx.fillStyle = ['#ff6b9d', '#ff4d7a', '#ff8a9e', '#ff6b9d', '#ff4d7a', '#ff8a9e'][i];
        ctx.shadowColor = '#ff6b9d';
        ctx.shadowBlur = 6;
        ctx.beginPath();
        ctx.arc(px, py, petalR, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.shadowBlur = 0;
      ctx.fillStyle = '#ffd93d';
      ctx.shadowColor = '#ffd93d';
      ctx.shadowBlur = 8;
      ctx.beginPath();
      ctx.arc(cx, cy, radius * 0.25, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 'planet': {
      const grad = ctx.createRadialGradient(cx - radius*0.3, cy - radius*0.3, 2, cx, cy, radius);
      grad.addColorStop(0, '#ff9a76');
      grad.addColorStop(0.4, '#e07a5f');
      grad.addColorStop(0.8, '#8b5cf6');
      grad.addColorStop(1, '#4c1d95');
      ctx.fillStyle = grad;
      ctx.shadowColor = '#8b5cf6';
      ctx.shadowBlur = 14;
      ctx.fillRect(cx - radius, cy - radius, radius*2, radius*2);
      ctx.shadowBlur = 0;
      ctx.strokeStyle = 'rgba(200,180,255,0.5)';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.ellipse(cx + 2, cy + 2, radius * 1.5, radius * 0.25, 0.2, 0, Math.PI * 2);
      ctx.stroke();
      break;
    }
    case 'rainbow': {
      const colors = ['#ff6b6b', '#ff9f43', '#feca57', '#54a0ff', '#5f27cd'];
      const stripeWidth = (radius * 2) / colors.length;
      for (let i = 0; i < colors.length; i++) {
        ctx.fillStyle = colors[i];
        ctx.shadowColor = colors[i];
        ctx.shadowBlur = 4;
        ctx.fillRect(cx - radius + i * stripeWidth, cy - radius, stripeWidth + 0.5, radius * 2);
      }
      ctx.shadowBlur = 0;
      ctx.strokeStyle = 'rgba(255,255,255,0.3)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.stroke();
      break;
    }
    case 'diamond': {
      ctx.shadowColor = '#60a5fa';
      ctx.shadowBlur = 14;
      const grad = ctx.createLinearGradient(cx - radius, cy - radius, cx + radius, cy + radius);
      grad.addColorStop(0, '#93c5fd');
      grad.addColorStop(0.3, '#bfdbfe');
      grad.addColorStop(0.6, '#60a5fa');
      grad.addColorStop(1, '#3b82f6');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.moveTo(cx, cy - radius);
      ctx.lineTo(cx + radius * 0.7, cy);
      ctx.lineTo(cx, cy + radius);
      ctx.lineTo(cx - radius * 0.7, cy);
      ctx.closePath();
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.strokeStyle = 'rgba(255,255,255,0.4)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(cx, cy - radius * 0.4);
      ctx.lineTo(cx + radius * 0.5, cy + radius * 0.2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cx, cy - radius * 0.4);
      ctx.lineTo(cx - radius * 0.5, cy + radius * 0.2);
      ctx.stroke();
      break;
    }
    default: {
      // 经典白（兜底）
      const grad = ctx.createRadialGradient(cx - radius*0.3, cy - radius*0.3, 2, cx, cy, radius);
      grad.addColorStop(0, '#ffffff');
      grad.addColorStop(0.7, '#e0f0ff');
      grad.addColorStop(1, '#a0c4e8');
      ctx.fillStyle = grad;
      ctx.shadowColor = '#52f6e8';
      ctx.shadowBlur = 12;
      ctx.fillRect(cx - radius, cy - radius, radius*2, radius*2);
      ctx.shadowBlur = 0;
      ctx.fillStyle = 'rgba(255,255,255,0.6)';
      ctx.beginPath();
      ctx.arc(cx - radius*0.25, cy - radius*0.25, radius*0.3, 0, Math.PI*2);
      ctx.fill();
    }
  }
  ctx.restore();
}

// ============================================================
// 绘制创意挡板预览（支持 default 经典）
// ============================================================
export function drawPaddlePreview(ctx, cx, cy, width, height, type) {
  ctx.save();
  const hw = width / 2;
  const hh = height / 2;
  roundedPath(ctx, cx - hw, cy - hh, width, height, 6);
  ctx.clip();

  switch (type) {
    case 'default': {
      const grad = ctx.createLinearGradient(cx - hw, cy, cx + hw, cy);
      grad.addColorStop(0, '#52f6e8');
      grad.addColorStop(0.5, '#ffffff');
      grad.addColorStop(1, '#52f6e8');
      ctx.fillStyle = grad;
      ctx.shadowColor = '#52f6e8';
      ctx.shadowBlur = 10;
      ctx.fillRect(cx - hw, cy - hh, width, height);
      break;
    }
    case 'stool': {
      ctx.fillStyle = '#d4a373';
      ctx.shadowColor = '#d4a373';
      ctx.shadowBlur = 6;
      ctx.fillRect(cx - hw, cy - hh, width, height);
      ctx.shadowBlur = 0;
      ctx.fillStyle = '#b8895e';
      for (let i = 0; i < 4; i++) {
        const x = cx - hw + 8 + i * (width - 16) / 3;
        ctx.fillRect(x, cy - 2, 3, 4);
      }
      ctx.fillStyle = '#a67c52';
      ctx.beginPath();
      ctx.arc(cx - hw + 8, cy + hh - 4, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(cx + hw - 8, cy + hh - 4, 4, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 'grass': {
      const grad = ctx.createLinearGradient(cx, cy - hh, cx, cy + hh);
      grad.addColorStop(0, '#7ec850');
      grad.addColorStop(0.5, '#5da83a');
      grad.addColorStop(1, '#3d8b2a');
      ctx.fillStyle = grad;
      ctx.shadowColor = '#5da83a';
      ctx.shadowBlur = 6;
      ctx.fillRect(cx - hw, cy - hh, width, height);
      ctx.shadowBlur = 0;
      for (let i = 0; i < 12; i++) {
        const x = cx - hw + 6 + i * (width - 12) / 11;
        const h = 4 + Math.random() * 8;
        ctx.fillStyle = i % 2 === 0 ? '#8dd86a' : '#6cb84a';
        ctx.fillRect(x, cy - hh - 2, 2, h);
      }
      break;
    }
    case 'paddle_rainbow': {
      const colors = ['#ff6b6b', '#ff9f43', '#feca57', '#54a0ff', '#5f27cd'];
      const stripeH = height / colors.length;
      for (let i = 0; i < colors.length; i++) {
        ctx.fillStyle = colors[i];
        ctx.shadowColor = colors[i];
        ctx.shadowBlur = 4;
        ctx.fillRect(cx - hw, cy - hh + i * stripeH, width, stripeH + 0.5);
      }
      break;
    }
    case 'carpet': {
      const grad = ctx.createLinearGradient(cx - hw, cy, cx + hw, cy);
      grad.addColorStop(0, '#8b5cf6');
      grad.addColorStop(0.3, '#a78bfa');
      grad.addColorStop(0.5, '#c4b5fd');
      grad.addColorStop(0.7, '#a78bfa');
      grad.addColorStop(1, '#8b5cf6');
      ctx.fillStyle = grad;
      ctx.shadowColor = '#8b5cf6';
      ctx.shadowBlur = 10;
      ctx.fillRect(cx - hw, cy - hh, width, height);
      ctx.shadowBlur = 0;
      ctx.strokeStyle = '#c4b5fd';
      ctx.lineWidth = 1.5;
      for (let i = 0; i < 7; i++) {
        const x = cx - hw + 6 + i * (width - 12) / 6;
        ctx.beginPath();
        ctx.moveTo(x, cy + hh);
        ctx.lineTo(x - 2 + i * 0.7, cy + hh + 6);
        ctx.stroke();
      }
      break;
    }
    case 'wave': {
      const grad = ctx.createLinearGradient(cx, cy - hh, cx, cy + hh);
      grad.addColorStop(0, '#60a5fa');
      grad.addColorStop(0.5, '#3b82f6');
      grad.addColorStop(1, '#1d4ed8');
      ctx.fillStyle = grad;
      ctx.shadowColor = '#3b82f6';
      ctx.shadowBlur = 8;
      ctx.fillRect(cx - hw, cy - hh, width, height);
      ctx.shadowBlur = 0;
      ctx.strokeStyle = 'rgba(255,255,255,0.3)';
      ctx.lineWidth = 1.5;
      for (let row = 0; row < 3; row++) {
        const y = cy - hh + 6 + row * (height - 12) / 2;
        ctx.beginPath();
        for (let x = cx - hw + 2; x < cx + hw - 2; x += 2) {
          const waveY = y + Math.sin(x * 0.3 + row * 1.5) * 4;
          if (x === cx - hw + 2) ctx.moveTo(x, waveY);
          else ctx.lineTo(x, waveY);
        }
        ctx.stroke();
      }
      break;
    }
    case 'mecha': {
      const grad = ctx.createLinearGradient(cx, cy - hh, cx, cy + hh);
      grad.addColorStop(0, '#64748b');
      grad.addColorStop(0.5, '#94a3b8');
      grad.addColorStop(1, '#475569');
      ctx.fillStyle = grad;
      ctx.shadowColor = '#64748b';
      ctx.shadowBlur = 8;
      ctx.fillRect(cx - hw, cy - hh, width, height);
      ctx.shadowBlur = 0;
      ctx.fillStyle = '#cbd5e1';
      for (let i = 0; i < 5; i++) {
        const x = cx - hw + 6 + i * (width - 12) / 4;
        ctx.beginPath();
        ctx.arc(x, cy - hh + 6, 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(x, cy + hh - 6, 3, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.fillStyle = '#38bdf8';
      ctx.shadowColor = '#38bdf8';
      ctx.shadowBlur = 8;
      ctx.fillRect(cx - hw + 4, cy - 1.5, width - 8, 3);
      break;
    }
    default: {
      const grad = ctx.createLinearGradient(cx - hw, cy, cx + hw, cy);
      grad.addColorStop(0, '#52f6e8');
      grad.addColorStop(0.5, '#ffffff');
      grad.addColorStop(1, '#52f6e8');
      ctx.fillStyle = grad;
      ctx.shadowColor = '#52f6e8';
      ctx.shadowBlur = 10;
      ctx.fillRect(cx - hw, cy - hh, width, height);
    }
  }
  ctx.restore();
}

// ============================================================
// 绘制皮肤卡片
// ============================================================
function drawSkinCard(ctx, rect, skin, category, language, isSelected) {
  drawGlassPanel(ctx, rect, {
    radius: 14,
    fill: "rgba(7, 38, 60, 0.88)",
    stroke: isSelected ? "rgba(255, 215, 0, 0.9)" : "rgba(82, 246, 232, 0.50)",
    glow: isSelected ? 14 : 6,
    lineWidth: isSelected ? 2.5 : 1.5
  });

  ctx.save();

  const centerX = rect.x + rect.width / 2;
  const centerY = rect.y + rect.height / 2 - 2;
  const previewSize = Math.min(rect.width * 0.32, 32);

  if (category === 'ball') {
    drawBallPreview(ctx, centerX, centerY, previewSize, skin.type || 'default');
  } else {
    drawPaddlePreview(ctx, centerX, centerY, previewSize * 1.6, previewSize * 0.4, skin.type || 'default');
  }

  ctx.shadowBlur = 0;
  ctx.textAlign = "center";
  ctx.textBaseline = "bottom";
  ctx.fillStyle = THEME.white;
  ctx.font = '600 11px "Microsoft YaHei", sans-serif';
  ctx.fillText(skinName(skin, language), rect.x + rect.width / 2, rect.y + rect.height - 8);

  if (isSelected) {
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillStyle = "#ffd700";
    ctx.font = '600 9px "Microsoft YaHei", sans-serif';
    ctx.fillText(skinText(language).selected, rect.x + rect.width / 2, rect.y + 6);
  }

  ctx.restore();
  return { rect, skin };
}

// ============================================================
// 主渲染函数
// ============================================================
export function renderSkin(ctx, data) {
  const { width, height } = getCanvasSize(ctx);
  const language = normalizeLanguage(data.language);
  const text = skinText(language);
  const selectedId = data.selectedId || 'ball_default';

  drawNeonBackground(ctx, { overlay: 0.30, decorations: true });

  const topBack = { x: 25, y: 26, width: 44, height: 34 };
  drawTopBar(ctx, text.title, { backRect: topBack });

  const categories = ['ball', 'paddle'];
  const tabHeight = 38;
  const tabGap = 12;
  const tabWidth = Math.min(120, (width - 60 - tabGap * (categories.length - 1)) / categories.length);
  const tabStartX = (width - (tabWidth * categories.length + tabGap * (categories.length - 1))) / 2;
  const tabY = 86;

  const selectedCategory = data.selectedCategory || 'ball';
  const tabRects = [];

  categories.forEach((cat, index) => {
    const rect = {
      x: tabStartX + index * (tabWidth + tabGap),
      y: tabY,
      width: tabWidth,
      height: tabHeight
    };
    const active = cat === selectedCategory;
    drawPillButton(ctx, rect, skinCategoryName(cat, language), {
      active: active,
      fontSize: 14,
      compact: true
    });
    tabRects.push({ rect, category: cat });
  });

  const cards = SKIN_DATA[selectedCategory] || [];
  const viewportTop = tabY + tabHeight + 14;
  const viewportBottom = height - 20;
  const viewportHeight = viewportBottom - viewportTop;

  const columns = 3;
  const gapX = 14;
  const gapY = 14;
  const sidePadding = 18;
  const cardWidth = (width - sidePadding * 2 - gapX * (columns - 1)) / columns;
  const cardHeight = cardWidth * 1.15;

  const totalCards = cards.length;
  const totalRows = Math.ceil(totalCards / columns);
  const contentHeight = totalRows * cardHeight + (totalRows - 1) * gapY;
  const maxScroll = Math.max(0, contentHeight - viewportHeight + 8);

  const scrollY = Number(data.scrollY) || 0;
  const clampedScroll = Math.max(0, Math.min(scrollY, maxScroll));

  const skinCards = [];

  ctx.save();
  ctx.beginPath();
  ctx.rect(0, viewportTop, width, viewportHeight);
  ctx.clip();

  cards.forEach((skin, index) => {
    const row = Math.floor(index / columns);
    const col = index % columns;
    const rect = {
      x: sidePadding + col * (cardWidth + gapX),
      y: viewportTop + 6 + row * (cardHeight + gapY) - clampedScroll,
      width: cardWidth,
      height: cardHeight
    };
    if (rect.y + rect.height >= viewportTop - 8 && rect.y <= viewportBottom + 8) {
      const isSelected = (skin.id === selectedId);
      drawSkinCard(ctx, rect, skin, selectedCategory, language, isSelected);
      skinCards.push({
        rect: rect,
        skin: skin,
        category: selectedCategory,
        selected: isSelected
      });
    }
  });

  ctx.restore();

  if (maxScroll > 0) {
    const barHeight = Math.max(20, viewportHeight * (viewportHeight / contentHeight));
    const barY = viewportTop + (clampedScroll / maxScroll) * (viewportHeight - barHeight);
    ctx.save();
    ctx.globalAlpha = 0.3;
    roundedPath(ctx, width - 8, barY, 4, barHeight, 4);
    ctx.fillStyle = "#52f6e8";
    ctx.fill();
    ctx.restore();
  }

  return {
    backBtn: topBack,
    tabRects: tabRects,
    skinCards: skinCards,
    selectedCategory: selectedCategory,
    maxScroll: maxScroll,
    currentScroll: clampedScroll,
    viewportTop: viewportTop,
    viewportBottom: viewportBottom
  };
}